import React, { useCallback, useState } from 'react';
import { Button, Checkbox, Flexer, Spacer, Tag, Text } from '@ontruck/orc';
import { CopyableLink } from 'modules/Common/Copy';
import PropTypes from 'prop-types';
import { resendInvitationLink, updateShipperUser } from 'modules/shippers/http';
import cx from 'classnames';
import Tippy from '@tippyjs/react';
import { useLocation } from 'react-router-dom';
import { injectIntl } from 'react-intl';

const COLUMN_WIDTHS = {
  name: '20%',
  email: '20%',
  phone: '10%',
  always_cc: '10%',
  is_active: '10%',
  contact_instructions: '15%',
  actions: '15%',
};

const ShipperDetailUsers = ({
  className,
  shipperUsers,
  onEditUser,
  onUserModified,
  openId,
  intl,
}) => {
  const [resendingId, setResendingId] = useState(null);
  const [resendingSuccessId, setResendingSuccessId] = useState(null);
  const [isSettingAlwaysCC, setIsSettingAlwaysCC] = useState();
  const [isSettingActive, setIsSettingActive] = useState();
  const location = useLocation();
  const formOpen = location.pathname.includes('user');

  const t = intl.messages.modules.shippers['shipper-detail-users'];

  const resendInvitation = async (userId, url) => {
    try {
      setResendingId(userId);
      await resendInvitationLink(url);
    } catch (e) {
      console.error(e);
    } finally {
      setResendingId(null);
      setResendingSuccessId(userId);
      setTimeout(() => {
        setResendingSuccessId(null);
      }, 5000);
    }
  };

  const setUserAlwaysCC = useCallback(
    async (userId, isAlwaysCC) => {
      try {
        setIsSettingAlwaysCC(userId);
        const modifiedUser = await updateShipperUser(userId, {
          always_cc: isAlwaysCC,
        });
        if (modifiedUser.always_cc === isAlwaysCC) {
          onUserModified(modifiedUser);
        }
      } catch (e) {
      } finally {
        setIsSettingAlwaysCC(null);
      }
    },
    [setIsSettingAlwaysCC, onUserModified]
  );

  const setUserIsActive = useCallback(
    async (userId, isActive) => {
      try {
        setIsSettingActive(userId);
        const modifiedUser = await updateShipperUser(userId, {
          is_active: isActive,
        });
        if (modifiedUser.is_active === isActive) {
          onUserModified(modifiedUser);
        }
      } catch (e) {
      } finally {
        setIsSettingActive(null);
      }
    },
    [setIsSettingActive, onUserModified]
  );

  const rowRenderer = useCallback(
    (user) => {
      if (!user) return null;
      const { id, name, email, always_cc, phone, is_active, contact_instructions } = user;

      const isResending = resendingId === user?.id;
      const hasResentWithSuccess = resendingSuccessId === user?.id;

      return (
        <Flexer.Parent
          key={id}
          className={cx('shipper-detail-users__row', {
            'shipper-detail-users__row--open': formOpen && `${id}` === openId,
            'shipper-detail-users__row--closed': !formOpen || `${id}` !== openId,
          })}
          alignItems="center"
        >
          <Flexer.Child flexBasis={COLUMN_WIDTHS.name}>{name || '-'}</Flexer.Child>
          <Flexer.Child flexBasis={COLUMN_WIDTHS.email}>
            <CopyableLink text={email} />
          </Flexer.Child>
          <Flexer.Child flexBasis={COLUMN_WIDTHS.phone}>
            {phone ? (
              <CopyableLink text={phone} />
            ) : (
              <Text color="accent900">{t.missing_phone}</Text>
            )}
          </Flexer.Child>
          <Flexer.Child flexBasis={COLUMN_WIDTHS.always_cc}>
            <Flexer.Parent justifyContent="center">
              <Checkbox
                name="always_cc"
                checked={always_cc}
                disabled={isSettingAlwaysCC === id}
                onChange={(_, { value }) => setUserAlwaysCC(id, value)}
              />
            </Flexer.Parent>
          </Flexer.Child>
          <Flexer.Child flexBasis={COLUMN_WIDTHS.is_active}>
            <Flexer.Parent justifyContent="center">
              <Checkbox
                name="is_active"
                checked={is_active}
                disabled={!is_active || isSettingActive === id}
                onChange={(_, { value }) => setUserIsActive(id, value)}
              />
            </Flexer.Parent>
          </Flexer.Child>
          <Flexer.Child flexBasis={COLUMN_WIDTHS.contact_instructions}>
            <>
              {!is_active ? (
                <Tag text={t.not_confirmed} skin="modernRed" weight="bold" size="M" />
              ) : (
                <>
                  {contact_instructions && (
                    <Tippy interactive maxWidth="none" content={contact_instructions}>
                      <span>{contact_instructions}</span>
                    </Tippy>
                  )}
                  {!contact_instructions && <span className="light">{t.no_instructions}</span>}
                </>
              )}
            </>
            <>
              {contact_instructions && (
                <Tippy interactive maxWidth="none" content={contact_instructions}>
                  <span>{contact_instructions}</span>
                </Tippy>
              )}
              {!contact_instructions && <span className="light">{t.no_instructions}</span>}
            </>
          </Flexer.Child>
          <Flexer.Child flexBasis={COLUMN_WIDTHS.actions}>
            <Flexer.Parent justifyContent="flex-end" className="shipper-detail-users__actions">
              {!is_active && (
                <Spacer mRight="S">
                  {!hasResentWithSuccess && (
                    <Button
                      ghost
                      skin="primary"
                      onClick={() => resendInvitation(user.id, user.resend_invitation)}
                      disabled={resendingSuccessId}
                    >
                      {isResending ? t.resending : t.resend_invitation}
                    </Button>
                  )}
                  {hasResentWithSuccess && <span className="success">{t.resend_success}</span>}
                </Spacer>
              )}
              <Button ghost skin="primary" onClick={() => onEditUser(user)}>
                {openId === `${id}` ? t.editing : t.edit}
              </Button>
            </Flexer.Parent>
          </Flexer.Child>
        </Flexer.Parent>
      );
    },
    [
      resendingId,
      resendingSuccessId,
      onEditUser,
      isSettingAlwaysCC,
      setUserAlwaysCC,
      isSettingActive,
      setUserIsActive,
      openId,
      formOpen,
      t,
    ]
  );

  return (
    <div className={className}>
      {/* Header */}
      <Flexer.Parent className="shipper-detail-users__header">
        <Flexer.Child flexBasis={COLUMN_WIDTHS.name}>{t.name}</Flexer.Child>
        <Flexer.Child flexBasis={COLUMN_WIDTHS.email}>{t.email}</Flexer.Child>
        <Flexer.Child flexBasis={COLUMN_WIDTHS.phone}>{t.phone}</Flexer.Child>
        <Flexer.Child flexBasis={COLUMN_WIDTHS.always_cc}>
          <Flexer.Parent justifyContent="center">{t.always_cc}</Flexer.Parent>
        </Flexer.Child>
        <Flexer.Child flexBasis={COLUMN_WIDTHS.is_active}>
          <Flexer.Parent justifyContent="center">{t.is_active}</Flexer.Parent>
        </Flexer.Child>
        <Flexer.Child mLeft="S" flexBasis={COLUMN_WIDTHS.contact_instructions}>
          {t.contact_instructions}
        </Flexer.Child>
        <Flexer.Child flexBasis={COLUMN_WIDTHS.actions}>
          <Flexer.Parent justifyContent="flex-end">{t.actions}</Flexer.Parent>
        </Flexer.Child>
      </Flexer.Parent>

      {/* Users list */}
      <div className="shipper-detail-users__list">{shipperUsers.map(rowRenderer)}</div>
    </div>
  );
};

ShipperDetailUsers.propTypes = {
  className: PropTypes.string,
  shipperUsers: PropTypes.array.isRequired,
  onEditUser: PropTypes.func.isRequired,
  onUserModified: PropTypes.func.isRequired,
};

ShipperDetailUsers.defaultProps = {
  shipperUsers: [],
};

export default injectIntl(ShipperDetailUsers);
